import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';

export const MEASURED_ROW_CELL_SELECTOR = '.mediocre-tableEditable-table--main tr td:first-child';
export const MAIN_TABLE_SELECTOR = 'table.mediocre-tableEditable-table--main';
export const TABLE_WRAPPER_SELECTOR = '.mediocre-tableEditable-tableWrapper';
export const HEADER_COLUMN_SELECTOR = '.mediocre-tableEditable-axisCell--header';
export const TABLE_NAME_SELECTOR = '.mediocre-tableEditable-tableName';

/**
 * Measurements closer than this (px) are the same size. `getBoundingClientRect` reports fractional
 * sizes that drift by hundredths of a pixel as content moves relative to the device pixel grid while
 * scrolling, without anything resizing; treating that as a change would re-render the table for
 * nothing.
 */
export const MEASUREMENT_TOLERANCE_PX = 0.1;

/**
 * The table geometry only the DOM knows. Everything derived from it together with props, such as the
 * table's fixed height, is computed by the table measurements selector.
 */
export interface TableGeometry {
  /** Height of each rendered row, column header row first. */
  rowHeights: Array<number>;
  /** Width of the table name in the title bar, or `undefined` when the title bar is suppressed. */
  headerWidth: number | undefined;
  /** Width of the row-header column, or 0 before it renders. */
  headerColumnWidth: number;
  tableWrapperWidth: number | undefined;
  pagePaddingTop: number;
  pagePaddingBottom: number;
}

export type MeasuredState = Pick<
  TableEditableState,
  | 'rowHeights'
  | 'headerWidth'
  | 'headerColumnWidth'
  | 'tableWrapperWidth'
  | 'pagePaddingTop'
  | 'pagePaddingBottom'
>;

export function isSameSize(a: number, b: number): boolean {
  return Math.abs(a - b) < MEASUREMENT_TOLERANCE_PX;
}

function isSameRowHeights(a: Array<number>, b: Array<number>): boolean {
  return a.length === b.length && a.every((height, i) => isSameSize(height, b[i]));
}

export function readTableGeometry(root: Element, scrollContainer: Element | null): TableGeometry {
  const rowHeights = Array.from(root.querySelectorAll(MEASURED_ROW_CELL_SELECTOR)).map(
    (cell) => cell.getBoundingClientRect().height
  );
  // The first row contains an extra top border which affects the height computation of the first row
  // However, this border is also applied to the first filler row, so we need to remove it here.
  if (rowHeights.length > 0) {
    rowHeights[0] -= 1;
  }
  const scrollContainerStyle = scrollContainer ? window.getComputedStyle(scrollContainer) : null;
  return {
    rowHeights,
    headerWidth: root.querySelector(TABLE_NAME_SELECTOR)?.getBoundingClientRect().width,
    headerColumnWidth: root.querySelector(HEADER_COLUMN_SELECTOR)?.getBoundingClientRect().width ?? 0,
    tableWrapperWidth: root.querySelector(TABLE_WRAPPER_SELECTOR)?.getBoundingClientRect().width,
    pagePaddingTop: (scrollContainerStyle && parseFloat(scrollContainerStyle.paddingTop)) || 0,
    pagePaddingBottom: (scrollContainerStyle && parseFloat(scrollContainerStyle.paddingBottom)) || 0,
  };
}

/**
 * The state writes that bring `state` up to date with `geometry`. An unchanged measurement, within {@link MEASUREMENT_TOLERANCE_PX}, produces no writes. A missing
 * table name or wrapper (not rendered) keeps the last measured width.
 */
export function diffTableGeometry(state: MeasuredState, geometry: TableGeometry): Partial<MeasuredState> {
  const update: Partial<MeasuredState> = {};
  if (!isSameRowHeights(state.rowHeights, geometry.rowHeights)) {
    update.rowHeights = geometry.rowHeights;
  }
  if (geometry.headerWidth != null && !isSameSize(state.headerWidth, geometry.headerWidth)) {
    update.headerWidth = geometry.headerWidth;
  }
  if (!isSameSize(state.headerColumnWidth, geometry.headerColumnWidth)) {
    update.headerColumnWidth = geometry.headerColumnWidth;
  }
  if (geometry.tableWrapperWidth && !isSameSize(state.tableWrapperWidth, geometry.tableWrapperWidth)) {
    update.tableWrapperWidth = geometry.tableWrapperWidth;
  }
  if (!isSameSize(state.pagePaddingTop, geometry.pagePaddingTop)) {
    update.pagePaddingTop = geometry.pagePaddingTop;
  }
  if (!isSameSize(state.pagePaddingBottom, geometry.pagePaddingBottom)) {
    update.pagePaddingBottom = geometry.pagePaddingBottom;
  }
  return update;
}
